import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BuiltBy, Logo } from "../components/Brand";
import { Icon, type IconName } from "../components/Icon";
import { LanguagePicker } from "../components/Pickers";
import { ShareCard } from "../components/ShareCard";
import { Spinner } from "../components/ui";
import { apiUrl } from "../lib/api";
import { useT } from "../lib/i18n";
import { applyTheme } from "../lib/theme";
import { hostTokenFor } from "../lib/storage";

// /r/:code — the room's front door. Mostly reached by someone who typed the
// code rather than scanning the QR, so it offers every door at once and only
// shows the admin one to a device that actually holds the host token.
export default function RoomEntry() {
  const { code = "" } = useParams<{ code: string }>();
  const t = useT();
  const [info, setInfo] = useState<{ title: string; theme: string } | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");
  const isHost = hostTokenFor(code) !== null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/rooms/${code}`));
        if (!res.ok) {
          if (!cancelled) setState("missing");
          return;
        }
        const data = (await res.json()) as { title: string; theme: string };
        if (cancelled) return;
        setInfo(data);
        // Match the room's look before the player has even joined.
        applyTheme(data.theme);
        setState("ready");
      } catch {
        if (!cancelled) setState("missing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (state === "loading") {
    return (
      <div className="min-h-full flex items-center justify-center gap-2 text-ink/50">
        <Spinner /> {t("connecting")}
      </div>
    );
  }

  if (state === "missing") {
    return (
      <div className="min-h-full flex flex-col items-center justify-center gap-5 p-8 text-center">
        <Icon name="doorOpen" size="2.5rem" className="text-ink/30" />
        <p className="text-lg text-ink/70">{t("roomNotFound")}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-control bg-accent text-onAccent font-semibold hover:bg-accentLight transition-colors"
        >
          <Icon name="arrowLeft" />
          {t("appName")}
        </Link>
      </div>
    );
  }

  const doors: { to: string; icon: IconName; label: string; primary: boolean }[] = [
    { to: `/r/${code}/play`, icon: "buzzer", label: t("joinRoom"), primary: true },
    { to: `/r/${code}/show`, icon: "monitor", label: t("showcase"), primary: false },
    ...(isHost
      ? [{ to: `/r/${code}/admin`, icon: "settings" as IconName, label: t("adminTitle"), primary: false }]
      : []),
  ];

  return (
    <div className="min-h-full flex flex-col">
      <header className="flex items-center gap-3 px-5 sm:px-8 py-4">
        <Logo size={28} />
        <div className="flex-1" />
        <LanguagePicker compact />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center gap-9 p-6">
        <h1 className="type-display text-3xl sm:text-4xl text-accentLight text-center">
          {info?.title ?? t("appName")}
        </h1>

        <ShareCard code={code} />

        <div className="flex flex-wrap gap-2.5 justify-center">
          {doors.map((d) => (
            <Link
              key={d.to}
              to={d.to}
              className={`inline-flex items-center gap-2 h-12 px-6 rounded-control font-semibold transition-colors ${
                d.primary
                  ? "bg-accent text-onAccent hover:bg-accentLight"
                  : "bg-ink/10 border border-ink/15 hover:bg-ink/[0.17]"
              }`}
            >
              <Icon name={d.icon} />
              {d.label}
            </Link>
          ))}
        </div>
      </main>

      <footer className="px-5 sm:px-8 py-6 border-t border-line">
        <BuiltBy />
      </footer>
    </div>
  );
}
